/**
 * Copyright 2020 New Light Technologies, Inc.
 *
 * With Supporting Sponsorship from the Federal Emergency Management Agency (Contract: GSA Stars II GS-06F-0968Z)
 * In accordance with FAR 52.227-14(c)(iii), New Light Technologies, Inc. grants to the Government and others acting on its behalf a paid-up, nonexclusive, irrevocable, worldwide license in such copyrighted computer software and data to reproduce, prepare derivative works, and perform publicly and display publicly (but not to distribute copies to the public) by or on behalf of the Government.
 * Any other use, distribution, reproduction, modification, or publication without the prior express written authorization of New Light Technologies, Inc. is strictly prohibited.
 * All other rights are reserved by their respective copyright holders.
 *
 */
import React from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import Fade from '@mui/material/Fade';
import Slide from '@mui/material/Slide';
import Grow from '@mui/material/Grow';
import Zoom from '@mui/material/Zoom';

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

type ToastProps = {
  message: string;
  open: boolean;
  severity?: 'success' | 'error' | 'warning' | 'info' | 'success';
  vertical?: 'top' | 'bottom';
  horizontal?: 'left' | 'right';
  transition?: 'fade' | 'slide' | 'grow' | 'zoom';
  autoHideDuration?: number;
};

export const Toast = ({
  message,
  open,
  severity = 'success',
  vertical = 'bottom',
  horizontal = 'right',
  transition = 'slide',
  autoHideDuration = 6000,
}: ToastProps) => {
  const [isOpen, setIsOpen] = React.useState(open);

  // The toast follows its `open` prop except after a local dismissal, so the
  // prop is tracked while rendering rather than from an effect, which would
  // paint the stale visibility once before correcting it.
  const [lastOpen, setLastOpen] = React.useState(open);
  if (lastOpen !== open) {
    setLastOpen(open);
    setIsOpen(open);
  }

  const handleClose = React.useCallback((event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setIsOpen(false);
  }, []);

  const transitionComponent = React.useMemo(() => {
    switch (transition) {
      case 'fade':
        return Fade;
      case 'slide':
        return Slide;
      case 'grow':
        return Grow;
      case 'zoom':
        return Zoom;
      default:
        return Slide;
    }
  }, [transition]);

  return (
    <Snackbar
      open={isOpen}
      autoHideDuration={autoHideDuration}
      onClose={handleClose}
      anchorOrigin={{ vertical, horizontal }}
      slots={{ transition: transitionComponent }}
    >
      <Alert onClose={handleClose} severity={severity} sx={{ width: '100%' }}>
        {message}
      </Alert>
    </Snackbar>
  );
};
